import React, { ReactElement, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import {
  Box,
  Typography,
  Button,
  ForwardIcon,
  IconButton,
  Dialog,
  CloseIcon,
  Link
} from "@carrotfertility/carotene-core"
import { CaseRateRenewalModalTopSection } from "../case-rate-renewals/CaseRateRenewalModalTopSection"
import { useStartRouting } from "#/components/carrot-plans/shared/useQueryStartRouting"
import { updateHasCheckInModalTriggeredThisSession } from "#/components/views/modal/utils/localStorageUtils"
import { useMemberActionTracking } from "#/services/memberActionTracking/useMemberActionTracking"
import { MemberActions } from "#/services/memberActionTracking/memberActions"
import { RoutingFlowType } from "#/components/carrot-plans/types/routingFlowType"
import { Paths } from "#/utils/Paths"
import { NonStaleDoneUsingCarrotModal } from "#/components/carrot-plans/case-rate-renewals/NonStaleDoneUsingCarrotModal"

export function CheckInTimeIntroContainer({
  origin,
  setToggleModalFromCta
}: {
  origin: "email" | "cta" | "fresh"
  setToggleModalFromCta?: (open: boolean) => void
}): ReactElement {
  const emailLink = origin === "email"
  const clickedLink = origin === "cta"
  const showDoneForNow = origin === "fresh"
  const [openDoneUsingCarrotModal, setOpenDoneUsingCarrotModal] = useState(false)
  const [openCheckInModal, setOpenCheckInModal] = React.useState(true)
  const { mutate: addMemberAction } = useMemberActionTracking()

  React.useEffect(() => {
    if (openCheckInModal) {
      addMemberAction({
        memberActionTaken: MemberActions.CHECK_IN_DIALOG_VIEWED,
        metaData: {
          origin
        }
      })
    }
  }, [addMemberAction, openCheckInModal, origin])

  function handleClose(): void {
    if (clickedLink) {
      setToggleModalFromCta(false)
    }
    if (emailLink || showDoneForNow) {
      updateHasCheckInModalTriggeredThisSession(true)
      setOpenCheckInModal(false)
    }
  }

  if (openDoneUsingCarrotModal) {
    return (
      <NonStaleDoneUsingCarrotModal
        openDoneUsingCarrotModal={openDoneUsingCarrotModal}
        setOpenDoneUsingCarrotModal={(open) => {
          updateHasCheckInModalTriggeredThisSession(true)
          setOpenCheckInModal(false)
          setOpenDoneUsingCarrotModal(open)
        }}
        onBack={() => {
          setOpenDoneUsingCarrotModal(false)
          setOpenCheckInModal(true)
        }}
      />
    )
  }

  if (!openCheckInModal) {
    return null
  }

  return (
    <CheckInTimeIntro
      open={openCheckInModal}
      onClose={handleClose}
      showDoneForNow={showDoneForNow}
      setOpenDoneUsingCarrotModal={() => setOpenDoneUsingCarrotModal(true)}
    />
  )
}

function CheckInTimeIntro({
  open,
  onClose,
  showDoneForNow,
  setOpenDoneUsingCarrotModal
}: {
  open: boolean
  onClose: () => void
  showDoneForNow: boolean
  setOpenDoneUsingCarrotModal: () => void
}): JSX.Element {
  const intl = useIntl()
  const { startRouting } = useStartRouting(RoutingFlowType.CHECK_IN_EXPERIENCE)
  const [isStartRoutingLoading, setIsStartRoutingLoading] = useState(false)

  function handleStartRoutingClick() {
    setIsStartRoutingLoading(true)
    startRouting()
  }

  return (
    <Dialog open={open} onClose={onClose} scroll="body" aria-labelledby="check-in-time-modal">
      <Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <IconButton onClick={onClose} aria-label={intl.formatMessage({ defaultMessage: "Close" })}>
            <CloseIcon />
          </IconButton>
        </Box>
        <CaseRateRenewalModalTopSection
          showImage
          imageSrc="/images/illo-underwater-desktop"
          imageAltText={intl.formatMessage({ defaultMessage: "Check-in illustration" })}
          header={intl.formatMessage({ defaultMessage: "Check-in time" })}
        />
        <Box>
          <Typography variant="body1" paddingY={(theme) => theme.spacing(theme.tokens.spacing.lg)}>
            <FormattedMessage defaultMessage="We want to make sure your Carrot Plan is best serving you and your journey. Please take a couple minutes to update us so we can add new suggestions to your plan." />
          </Typography>
          <Typography variant="body1">
            <FormattedMessage
              defaultMessage="Have an update to share but don’t need a new plan? <link>Send a message</link> to your dedicated Carrot Companion anytime."
              values={{
                link: (linkContent) => (
                  <Link color="inherit" href={Paths.MESSAGES}>
                    {linkContent}
                  </Link>
                )
              }}
            />
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            paddingBlockStart: (theme) => theme.tokens.spacing.xxl
          }}
        >
          {showDoneForNow && (
            <Button variant="text" color="secondary" onClick={setOpenDoneUsingCarrotModal}>
              <FormattedMessage defaultMessage="I'm done using Carrot" />
            </Button>
          )}
          <Button
            color="secondary"
            variant="outlined"
            disabled={isStartRoutingLoading}
            onClick={handleStartRoutingClick}
            endIcon={<ForwardIcon />}
          >
            <FormattedMessage defaultMessage="Continue" />
          </Button>
        </Box>
      </Box>
    </Dialog>
  )
}
